// src/components/admin/BooksWithUsers.jsx

import React, { useEffect, useState } from "react";
import { HiRefresh, HiInformationCircle, HiEye, HiSearch } from "react-icons/hi";
import { motion } from "framer-motion";
import OrderManager from "../../services/dashboard/OrderManager";

export default function BooksWithUsers() {
  const [withMeOrders, setWithMeOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  // Fetch all "BOOK_WITH_ME" orders on mount (and on refresh):
  const fetchWithMeOrders = async () => {
    setLoading(true);
    try {
      const orders = await OrderManager.getBooksWithUsers();
      setWithMeOrders(Array.isArray(orders) ? orders : []);
    } catch (err) {
      console.error("Failed to fetch books-with-me:", err);
      setWithMeOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWithMeOrders();
  }, []);

  // Filter orders based on search term
  const filteredOrders = withMeOrders.filter(order => 
    order.book.bookName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    `${order.user.firstName} ${order.user.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.orderId.toString().includes(searchTerm)
  );

  // Generate user initials for avatar
  const getUserInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  // Calculate days ago
  const getDaysAgo = (creationDate) => {
    const [year, month, day] = creationDate;
    const orderDate = new Date(year, month - 1, day);
    const today = new Date();
    const diffTime = Math.abs(today - orderDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  return (
    <div className="p-6 bg-gray-50 dark:bg-slate-950 min-h-screen">
      <div className="max-w-5xl mx-auto">
        {/* Header - Light/Dark Compatible */}
        <header className="flex items-start justify-between mb-5">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Books with Users
            </h1>
            <p className="text-gray-600 dark:text-slate-400 mt-1.5 text-sm">
              Live view of issued books currently with readers
            </p>
          </div>
          <motion.button
            onClick={fetchWithMeOrders}
            whileHover={{ rotate: 180 }}
            whileTap={{ scale: 0.95 }}
            transition={{ duration: 0.3 }}
            className="p-3 rounded-xl bg-white dark:bg-slate-900/50 hover:bg-gray-50 dark:hover:bg-slate-800/70 transition-all duration-200 group shadow-lg hover:shadow-xl border border-gray-200 dark:border-slate-700/50 hover:border-orange-500/30 backdrop-blur-sm"
            title="Refresh"
            aria-label="Refresh list"
          >
            <HiRefresh className="w-5 h-5 text-gray-600 dark:text-slate-400 group-hover:text-orange-500 dark:group-hover:text-orange-400 transition-colors" />
          </motion.button>
        </header>

        {/* Info Banner - Light/Dark Compatible */}
        <div className="flex items-center gap-3 mb-6 p-4 bg-blue-50 dark:bg-slate-900/60 border border-blue-200 dark:border-slate-700/50 rounded-2xl shadow-lg backdrop-blur-sm">
          <HiInformationCircle className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0 drop-shadow-sm" />
          <p className="text-sm text-blue-800 dark:text-slate-300 font-medium">
            Showing all orders that have reached "Book with me" status
          </p>
        </div>

        {/* Search Bar - Light/Dark Glass */}
        <div className="mb-7">
          <div className="relative max-w-md">
            <HiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500 dark:text-slate-500" />
            <input
              type="text"
              placeholder="Search by book, user, or order ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3.5 bg-white dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700/50 rounded-2xl focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500/50 transition-all duration-200 text-sm shadow-lg hover:shadow-xl focus:shadow-2xl backdrop-blur-sm text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-slate-500 focus:bg-white dark:focus:bg-slate-900/80"
            />
          </div>
        </div>

        {/* Content - Light/Dark Compatible Cards */}
        {loading ? (
          <div className="flex items-center justify-center py-28">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
            <span className="ml-3 text-gray-600 dark:text-slate-300 font-medium">Loading...</span>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="text-center py-28">
            <div className="text-gray-500 dark:text-slate-400 text-lg mb-2 font-medium">
              {searchTerm ? "No matching results found" : "No books are currently with users"}
            </div>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="text-orange-500 dark:text-orange-400 hover:text-orange-600 dark:hover:text-orange-300 text-sm font-medium transition-colors"
              >
                Clear search
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredOrders.map((order, index) => {
              const [year, month, day, hour = 0, minute = 0] = order.creationDate;
              const dateTime = new Date(year, month - 1, day, hour, minute);
              const formattedDate = dateTime.toLocaleDateString("en-IN", {
                year: "numeric",
                month: "short",
                day: "numeric",
              });
              const formattedTime = dateTime.toLocaleTimeString("en-IN", {
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
              });
              const daysAgo = getDaysAgo(order.creationDate);

              return (
                <motion.div
                  key={order.orderId}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className="group bg-white dark:bg-slate-900/70 backdrop-blur-sm rounded-2xl border border-gray-200 dark:border-slate-700/50 p-4 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 hover:border-orange-500/30 hover:bg-gray-50 dark:hover:bg-slate-900/90 shadow-lg dark:shadow-xl hover:shadow-orange-500/5"
                >
                  <div className="flex flex-col gap-3">
                    {/* Top Section - Order ID + Status */}
                    <div className="flex items-center justify-between gap-2">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-mono font-bold bg-gray-100 dark:bg-slate-800/60 text-gray-700 dark:text-slate-300 shadow-lg border border-gray-200 dark:border-slate-600/30 backdrop-blur-sm">
                        #{order.orderId}
                      </span>
                      <motion.span 
                        whileHover={{ scale: 1.02 }}
                        className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 dark:bg-emerald-900/60 text-emerald-700 dark:text-emerald-300 shadow-lg border border-emerald-200 dark:border-emerald-700/50 group-hover:shadow-emerald-500/20 group-hover:bg-emerald-200 dark:group-hover:bg-emerald-900/80 transition-all duration-200 backdrop-blur-sm"
                      >
                        <span className="w-1.5 h-1.5 bg-emerald-500 dark:bg-emerald-400 rounded-full mr-1.5 animate-pulse"></span>
                        Active
                      </motion.span>
                    </div>

                    {/* Book Info */}
                    <div className="min-w-0">
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white truncate mb-1 group-hover:text-orange-600 dark:group-hover:text-orange-300 transition-colors duration-200 drop-shadow-sm" title={order.book.bookName}>
                        {order.book.bookName}
                      </h3>
                      <p className="text-xs text-gray-600 dark:text-slate-400 font-medium">
                        Issued book • Currently reading
                      </p>
                    </div>

                    {/* User + Date Section */}
                    <div className="flex items-center justify-between gap-3 pt-2 border-t border-gray-200 dark:border-slate-700/50">
                      {/* User Info */}
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="relative group/avatar flex-shrink-0">
                          <motion.div 
                            whileHover={{ scale: 1.05 }}
                            className="w-9 h-9 rounded-full bg-gradient-to-br from-orange-500 via-pink-500 to-purple-600 flex items-center justify-center text-white text-xs font-bold shadow-xl ring-2 ring-gray-200 dark:ring-slate-700/50 group-hover:ring-orange-500/30 transition-all duration-300"
                          >
                            {getUserInitials(order.user.firstName, order.user.lastName)}
                          </motion.div>
                          <div className="absolute -inset-2 rounded-full bg-gradient-to-br from-orange-500/20 to-purple-600/20 opacity-0 group-hover/avatar:opacity-100 transition-opacity duration-300 blur-lg -z-10"></div>
                        </div>
                        <div className="min-w-0">
                          <div className="font-semibold text-gray-900 dark:text-white text-xs truncate drop-shadow-sm">
                            {order.user.firstName} {order.user.lastName}
                          </div>
                        </div>
                      </div>

                      {/* Date Info */}
                      <div className="text-right flex-shrink-0">
                        <div className="font-bold text-gray-900 dark:text-white text-xs mb-0.5 drop-shadow-sm">
                          {formattedDate}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-slate-400 space-x-1">
                          <span className="font-medium">{formattedTime}</span>
                        </div>
                      </div>
                    </div>

                    {/* Days Ago Info */}
                    <div className="text-xs text-gray-400 dark:text-slate-500 text-center">
                      {daysAgo} days ago
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}